'use client';

import * as React from 'react';

/**
 * WEB-291 — Replace `aria-hidden` focus toggles with the `inert` attribute.
 *
 * ## Why this exists
 *
 * Our `Dialog` / `Sheet` primitives wrap `@radix-ui/react-dialog`. In modal
 * mode Radix calls `hideOthers()` from the `aria-hidden` package, which walks
 * the DOM and stamps `aria-hidden="true"` (plus a `data-aria-hidden` marker)
 * onto every sibling of the dialog content — including the document `<main>`
 * that holds the Discover view-tabs.
 *
 * If focus is still inside that background subtree while it gets
 * `aria-hidden="true"` (e.g. tabbing through the Discover view-tabs as the
 * Filters sheet shifts focus), Chrome logs:
 *
 *   "Blocked aria-hidden on an element because its descendant retained focus.
 *    The focus must not be hidden from assistive technology users. Avoid
 *    using aria-hidden on a focused element or its ancestor. Consider using
 *    the inert attribute instead..."
 *
 * Radix `react-dialog@1.1.x` hardcodes `hideOthers` (aria-hidden) and exposes
 * no prop to switch it to `inertOthers` (inert), and it does not export the
 * lower-level content impl we'd need to swap the behaviour cleanly. Forking
 * Radix or monkeypatching the `aria-hidden` package would be brittle across
 * dependency bumps.
 *
 * Instead, this hook owns a small, version-stable adapter: while a modal is
 * open it converts Radix's background `aria-hidden` markers into the native
 * `inert` attribute (which removes the subtree from the tab order *and* the
 * accessibility tree, with no focus-retention warning), then restores the
 * original DOM on cleanup. A `MutationObserver` makes this resilient to React
 * effect ordering — we react to the markers Radix actually applies rather than
 * assuming our effect runs after Radix's.
 *
 * ## Browser support
 *
 * Native `inert` is supported by every browser in this app's `browserslist`
 * (min Chrome 109 / Firefox 140 / Safari 26 / Edge 145; `inert` shipped in
 * Chrome 102, Firefox 112, Safari 15.5). No polyfill is warranted.
 */

/** Marker attribute the `aria-hidden` package stamps on suppressed siblings. */
const RADIX_ARIA_HIDDEN_MARKER = 'data-aria-hidden';

/** Our own marker so cleanup only touches nodes we converted. */
const INERT_OWNED_MARKER = 'data-loci-inert-bg';

/**
 * Per-node ownership state, shared across *every* hook instance via the
 * module-level {@link inertOwnership} map.
 *
 * `count` is a ref-count of how many live modal instances currently want this
 * node suppressed. `hadInert` is the node's *pre-modal* `inert` state, captured
 * exactly once on the 0→1 transition so a later overlapping instance — which
 * would observe the `inert` *we* set — can never mistake it for app-owned.
 */
interface InertOwnershipState {
  count: number;
  hadInert: boolean;
}

/**
 * Shared, cross-instance ownership ledger keyed by the background node. Keeping
 * this at module scope (not per hook instance) is what makes nested/overlapping
 * modals ownership-safe: each `convertNodeToInert` call increments the same
 * counter, so `inert` is only stripped once the *last* owning modal closes —
 * never while an outer modal is still open, never leaked after all close.
 */
const inertOwnership = new WeakMap<HTMLElement, InertOwnershipState>();

/**
 * Convert a single Radix-suppressed background node from `aria-hidden` to
 * `inert`, ref-counted across all hook instances. Returns a restore function
 * that releases this instance's claim; the node is only fully restored when the
 * last owning modal releases. The returned closure is idempotent (safe to call
 * once; extra calls are no-ops) so a single instance can never over-decrement.
 */
function convertNodeToInert(node: HTMLElement): () => void {
  // `aria-hidden` is always cleared while *any* modal owns the node — it must
  // never coexist with `inert` (double suppression) regardless of ref-count.
  node.removeAttribute('aria-hidden');

  let state = inertOwnership.get(node);
  if (!state) {
    // 0→1 transition: capture the genuine pre-modal state and apply `inert`.
    state = { count: 0, hadInert: node.hasAttribute('inert') };
    if (!state.hadInert) {
      node.setAttribute('inert', '');
    }
    node.setAttribute(INERT_OWNED_MARKER, 'true');
    inertOwnership.set(node, state);
  }
  state.count += 1;

  let released = false;

  return () => {
    if (released) return;
    released = true;

    const current = inertOwnership.get(node);
    if (!current) return;

    current.count -= 1;
    if (current.count > 0) {
      // An outer modal still owns this node — leave it suppressed.
      return;
    }

    // 1→0 transition: last owner released. Restore precisely.
    if (!current.hadInert) {
      node.removeAttribute('inert');
    }
    node.removeAttribute(INERT_OWNED_MARKER);
    inertOwnership.delete(node);

    // Re-stamp aria-hidden only if Radix still considers this node hidden.
    // Radix's own cleanup removes the marker; if the marker is gone we leave
    // the node fully restored (no aria-hidden, no inert).
    if (node.hasAttribute(RADIX_ARIA_HIDDEN_MARKER)) {
      node.setAttribute('aria-hidden', 'true');
    }
  };
}

/**
 * While `active` is true, replace Radix's background `aria-hidden="true"`
 * suppression with the `inert` attribute. No-op on the server and when
 * `active` is false.
 */
export function useInertBackground(active: boolean): void {
  React.useEffect(() => {
    if (!active || typeof document === 'undefined') return;

    const restores = new Map<HTMLElement, () => void>();

    const sync = (): void => {
      const marked = document.querySelectorAll<HTMLElement>(`[${RADIX_ARIA_HIDDEN_MARKER}]`);
      const seen = new Set<HTMLElement>();

      marked.forEach((node) => {
        seen.add(node);
        if (!restores.has(node)) {
          restores.set(node, convertNodeToInert(node));
        } else if (node.getAttribute('aria-hidden') === 'true') {
          // Radix re-stamped `aria-hidden` on a node we already converted
          // (e.g. a nested dialog re-ran `hideOthers`). Strip it again so the
          // node is suppressed by `inert` alone, never both.
          node.removeAttribute('aria-hidden');
        }
      });

      // A node Radix un-marked (e.g. nested dialog closed) must be restored.
      restores.forEach((restore, node) => {
        if (!seen.has(node)) {
          restore();
          restores.delete(node);
        }
      });
    };

    sync();

    const observer = new MutationObserver(sync);
    observer.observe(document.body, {
      attributes: true,
      subtree: true,
      attributeFilter: ['aria-hidden', RADIX_ARIA_HIDDEN_MARKER],
    });

    return () => {
      observer.disconnect();
      restores.forEach((restore) => restore());
      restores.clear();
    };
  }, [active]);
}
